import { sanitizeHtml } from '../utils/safeHtml';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import radiologyResultService from '../services/radiologyResultService';
import radiologyConfigService from '../services/radiologyConfigService';
import RadiologyRichEditor from '../components/radiology/RadiologyRichEditor';
import RadiologyTemplatePickerModal from '../components/radiology/RadiologyTemplatePickerModal';
import DoctorRadiologyPreferencesModal from '../components/radiology/DoctorRadiologyPreferencesModal';
import RadiologyTechnicianStudyModal from '../components/radiology/RadiologyTechnicianStudyModal';
import RadiologyPreviousReportsModal from '../components/radiology/RadiologyPreviousReportsModal';
import RadiologyStructuredFindingsDrawer from '../components/radiology/RadiologyStructuredFindingsDrawer';
import RadiologyBulkDocxModal from '../components/radiology/RadiologyBulkDocxModal';
import RadiologyQualityCheckModal from '../components/radiology/RadiologyQualityCheckModal';
import RadiologyAmendmentModal from '../components/radiology/RadiologyAmendmentModal';
import radiologyVoiceService from '../services/radiologyVoiceService';
import radiologyQualityChecker from '../services/radiologyQualityChecker';
import CommonPagination from '../components/common/CommonPagination';
import PAGINATION_CONFIG from '../config/paginationConfig';
import './RadiologyResultPage.css';

export default function RadiologyResultPage() {
  const navigate = useNavigate(); // eslint-disable-line no-unused-vars
  const [summary, setSummary] = useState({ pendingResults: 0, drafts: 0, approvedToday: 0, totalToday: 0 });
  const [roleView, setRoleView] = useState('DOCTOR');
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [resolvedConfig, setResolvedConfig] = useState(null);
  const [filters, setFilters] = useState({ uhid: '', patientName: '', registrationNo: '', barcodeNo: '', status: 'ALL', priority: 'ALL', fromDate: '', toDate: '' });
  const [activeSavedView, setActiveSavedView] = useState('ALL');
  const [worklist, setWorklist] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGINATION_CONFIG.defaultPageSize);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [activeReport, setActiveReport] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [showTemplatePickerModal, setShowTemplatePickerModal] = useState(false);
  const [showTechModal, setShowTechModal] = useState(false);
  const [techModalTestId, setTechModalTestId] = useState(null);
  const [techModalOrder, setTechModalOrder] = useState({});
  const [showPrevReportsModal, setShowPrevReportsModal] = useState(false);
  const [showStructuredDrawer, setShowStructuredDrawer] = useState(false);
  const [showBulkDocxModal, setShowBulkDocxModal] = useState(false);
  const [showQualityModal, setShowQualityModal] = useState(false);
  const [showAmendmentModal, setShowAmendmentModal] = useState(false);
  const [copiedFromNotice, setCopiedFromNotice] = useState(null);
  const [showInlineTemplatePicker, setShowInlineTemplatePicker] = useState(false);
  const [inlineTemplateSearch, setInlineTemplateSearch] = useState('');
  const [noTemplateWarning, setNoTemplateWarning] = useState(false);
  const [isVoiceListening, setIsVoiceListening] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [voiceToast, setVoiceToast] = useState(null);
  const [suggestedImpression, setSuggestedImpression] = useState('');
  const [suggestionIgnored, setSuggestionIgnored] = useState(false);
  const [recentTemplates, setRecentTemplates] = useState([]);
  const [favoriteTemplateIds, setFavoriteTemplateIds] = useState([]);
  const [exportingDocx, setExportingDocx] = useState(false);
  // ── New state for split-panel workbench ──────────────────────────────────
  const [worklistCollapsed, setWorklistCollapsed] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [patientCtxCollapsed, setPatientCtxCollapsed] = useState(false);
  const fileInputRef = useRef(null);
  const findingsEditorRef = useRef(null);
  const [formData, setFormData] = useState({
    examinationTechnique: '', clinicalHistory: '', findings: '', impression: '',
    recommendation: '', remarks: '', templateId: null, templateName: '',
    templateVersion: 1, priority: 'Routine', isCritical: false, criticalRemarks: '',
    reportStatusText: 'Final', revisionReason: '', amendmentReason: '',
    version: 1, isCopiedFromPrevious: false, copiedFromTestId: null
  });
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditTrail, setAuditTrail] = useState([]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await radiologyResultService.getSummary();
      if (res.success && res.data) setSummary(res.data);
    } catch (err) { console.error('Summary fetch error:', err); }
  }, []);

  const fetchWorklist = useCallback(async (customPage = pageNumber, customPageSize = pageSize) => {
    setLoading(true); setErrorMsg(null);
    try {
      const query = { ...filters, priority: filters.priority === 'ALL' ? undefined : filters.priority, roleView, pageNumber: customPage, pageSize: customPageSize };
      const res = await radiologyResultService.getWorklist(query);
      if (res.success && res.data) { setWorklist(res.data.items || []); setTotalCount(res.data.totalCount || 0); }
    } catch (err) { setErrorMsg(err.response?.data?.message || 'Failed to load radiology worklist.'); }
    finally { setLoading(false); }
  }, [filters, roleView, pageNumber, pageSize]);

  // Reset page to 1 whenever search/filter criteria change
  useEffect(() => {
    setPageNumber(1);
  }, [filters, activeSavedView, roleView]);

  useEffect(() => { fetchSummary(); fetchWorklist(pageNumber, pageSize); }, [fetchSummary, fetchWorklist, pageNumber, pageSize]);

  const loadDoctorQuickTemplates = useCallback((docId) => {
    try {
      setFavoriteTemplateIds(JSON.parse(localStorage.getItem(`rad_favorites_${docId || 'default'}`) || '[]'));
      setRecentTemplates(JSON.parse(localStorage.getItem(`rad_recent_templates_${docId || 'default'}`) || '[]'));
    } catch (e) { console.warn('Could not read quick templates:', e); }
  }, []);

  const recordRecentTemplate = (tmpl) => {
    try {
      const key = `rad_recent_templates_${activeReport?.doctorId || 'default'}`;
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      const updated = [tmpl, ...existing.filter(t => t.templateId !== tmpl.templateId)].slice(0, 5);
      localStorage.setItem(key, JSON.stringify(updated));
      setRecentTemplates(updated);
    } catch (e) { console.warn('Could not record recent template:', e); }
  };

  const handleSelectTemplate = (tmpl) => {
    if (!tmpl) return;
    const desc = tmpl.templateDesc || '';
    let findingsContent = desc, impressionContent = '';
    if (/IMPRESSION:/i.test(desc)) {
      const parts = desc.split(/IMPRESSION:/i);
      findingsContent = parts[0].replace(/FINDINGS:|CLINICAL FINDINGS:/i, '').trim();
      impressionContent = parts[1].trim();
    }
    setFormData(prev => ({ ...prev, findings: findingsContent, impression: impressionContent || prev.impression, templateId: tmpl.templateId, templateName: tmpl.templateTitle || tmpl.title || '', templateVersion: tmpl.version || 1 }));
    setSelectedTemplateId(String(tmpl.templateId));
    recordRecentTemplate(tmpl);
  };

  const handleStartBlank = () => {
    setFormData(prev => ({ ...prev, findings: '', impression: '', templateId: null, templateName: '' }));
    setSelectedTemplateId('');
  };

  const handleMarkAllNormal = () => {
    if (!activeReport) return;
    const itemLower = (activeReport.itemName || '').toLowerCase();
    let nf = '', ni = '';
    if (itemLower.includes('chest') || itemLower.includes('thorax')) {
      nf = '<p><strong>CLINICAL FINDINGS:</strong><br>Trachea central. Both lung fields clear. No consolidation, collapse, or infiltrates. Normal cardiothoracic ratio. Clear costophrenic angles. Unremarkable bony structures.</p>';
      ni = 'Normal chest radiograph. No acute cardiopulmonary abnormality.';
    } else if (itemLower.includes('brain') || itemLower.includes('head')) {
      nf = '<p><strong>CLINICAL FINDINGS:</strong><br>Normal gray-white matter differentiation. Basal ganglia and brainstem within normal limits. Ventricular system unremarkable. No infarction, hemorrhage, or mass lesion.</p>';
      ni = 'Normal brain imaging. No acute intracranial pathology.';
    } else {
      nf = '<p><strong>CLINICAL FINDINGS:</strong><br>Liver: Normal size and echotexture. Gallbladder: Normal, no calculus. Pancreas: Normal. Spleen: Normal. Kidneys: Both normal, no calculus or hydronephrosis. Peritoneal cavity: No free fluid.</p>';
      ni = 'Normal ultrasound examination. No focal abnormality detected.';
    }
    setFormData(prev => ({ ...prev, findings: nf, impression: ni }));
  };

  const handleInsertStructuredFindings = ({ findingsHtml, impressionText }) => {
    setFormData(prev => ({ ...prev, findings: (prev.findings ? prev.findings + '<br>' : '') + findingsHtml, impression: impressionText || prev.impression }));
  };

  const handleCopyPreviousReport = (prevReport) => {
    if (!prevReport) return;
    setFormData(prev => ({ ...prev, examinationTechnique: prevReport.examinationTechnique || prev.examinationTechnique, clinicalHistory: prevReport.clinicalHistory || prev.clinicalHistory, findings: prevReport.findings || '', impression: prevReport.impression || '', recommendation: prevReport.recommendation || '', isCopiedFromPrevious: true, copiedFromTestId: prevReport.testId }));
    setCopiedFromNotice(`Copied from Test #${prevReport.testId} (${prevReport.itemName}). Review before finalizing.`);
    setSuccessMsg('Prior report findings loaded into active transaction.'); setTimeout(() => setSuccessMsg(null), 4500);
  };

  const handleExportDocx = async (testId) => {
    if (!testId) return;
    setExportingDocx(true);
    try {
      const blob = await radiologyResultService.exportReportDocx(testId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `Radiology_Report_${activeReport?.labNo || testId}.docx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      setSuccessMsg('Report exported as DOCX.'); setTimeout(() => setSuccessMsg(null), 3500);
    } catch (err) { setErrorMsg('Failed to export DOCX.'); }
    finally { setExportingDocx(false); }
  };

  const handleDocxFileChange = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const res = await radiologyResultService.extractDocx(file);
      if (res.success && res.data) { setFormData(prev => ({ ...prev, findings: (prev.findings ? prev.findings + '<br>' : '') + (res.data.extractedHtml || '') })); setSuccessMsg(`Extracted DOCX content from '${file.name}'.`); setTimeout(() => setSuccessMsg(null), 4000); }
    } catch (err) { setErrorMsg(err.response?.data?.message || 'Failed to extract DOCX.'); }
  };

  const handleSaveDraft = async () => {
    if (!activeReport) return;
    setActionLoading(true); setErrorMsg(null);
    try {
      const res = await radiologyResultService.saveDraft(activeReport.testId, formData);
      setSuccessMsg(res.message || 'Draft saved successfully.'); setActiveReport(prev => ({ ...prev, approved: 0 })); fetchSummary(); fetchWorklist();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) { setErrorMsg(err.response?.data?.message || 'Failed to save draft.'); }
    finally { setActionLoading(false); }
  };

  const handleFinalize = async () => {
    if (!activeReport) return;
    setActionLoading(true); setErrorMsg(null);
    try {
      const res = await radiologyResultService.finalizeReport(activeReport.testId, formData);
      setSuccessMsg(res.message || 'Report finalized and signed off.'); setActiveReport(prev => ({ ...prev, approved: 1 })); fetchSummary(); fetchWorklist();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) { setErrorMsg(err.response?.data?.message || 'Failed to finalize report.'); }
    finally { setActionLoading(false); }
  };

  const handleSaveAndApprove = async () => {
    if (!activeReport) return;
    await handleFinalize();
  };

  const handleConfirmRevision = async () => {
    if (!activeReport) return;
    if (!formData.revisionReason?.trim()) { setErrorMsg('A clinical revision reason is required.'); return; }
    setActionLoading(true); setErrorMsg(null);
    try {
      const res = await radiologyResultService.reviseReport(activeReport.testId, formData);
      setSuccessMsg(res.message || 'Report revised.'); setShowRevisionModal(false); fetchSummary(); fetchWorklist();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) { setErrorMsg(err.response?.data?.message || 'Failed to revise report.'); }
    finally { setActionLoading(false); }
  };

  const handleViewAudit = async (testId) => {
    setActionLoading(true);
    try {
      const res = await radiologyResultService.getAuditTrail(testId);
      if (res.success && res.data) { setAuditTrail(res.data); setShowAuditModal(true); }
    } catch (err) { setErrorMsg('Failed to load audit history.'); }
    finally { setActionLoading(false); }
  };

  const handleOpenReport = async (testId) => {
    setActionLoading(true); setErrorMsg(null); setCopiedFromNotice(null);
    setShowInlineTemplatePicker(false); setNoTemplateWarning(false);
    try {
      const res = await radiologyResultService.getReportDetail(testId);
      if (res.success && res.data) {
        const d = res.data;
        setActiveReport(d);
        setFormData({ examinationTechnique: d.examinationTechnique || '', clinicalHistory: d.clinicalHistory || '', findings: d.findings || '', impression: d.impression || '', recommendation: d.recommendation || '', remarks: d.remarks || '', templateId: d.templateId || null, templateName: d.templateName || '', templateVersion: d.templateVersion || 1, studyInstanceUid: d.studyInstanceUid || null, accessionNumber: d.accessionNumber || d.labNo || null, priority: d.priority || 'Routine', isCritical: !!d.isCritical, criticalRemarks: d.criticalRemarks || '', reportStatusText: d.reportStatusText || 'Final', revisionReason: '', amendmentReason: d.amendmentReason || '', version: d.version || 1, isCopiedFromPrevious: false, copiedFromTestId: null });
        setSuggestionIgnored(false);
        loadDoctorQuickTemplates(d.doctorId);
        try {
          const configRes = await radiologyConfigService.resolveConfiguration({ centreId: d.centreId, doctorId: d.doctorId, investigationId: d.investigationId });
          if (configRes.success && configRes.data) setResolvedConfig(configRes.data);
        } catch (cfgErr) { console.warn('Config resolution fallback:', cfgErr); }
        const templates = d.templates || [];
        if (!d.findings) {
          if (templates.length === 1) { setTimeout(() => { handleSelectTemplate(templates[0]); setShowInlineTemplatePicker(false); setNoTemplateWarning(false); }, 50); }
          else if (templates.length > 1) {
            const def = templates.find(t => t.isDefault);
            if (def && !d.templateId) { setTimeout(() => { handleSelectTemplate(def); setShowInlineTemplatePicker(false); }, 50); }
            else if (d.templateId) { setSelectedTemplateId(String(d.templateId)); setShowInlineTemplatePicker(false); }
            else { setSelectedTemplateId(''); setShowInlineTemplatePicker(true); }
            setNoTemplateWarning(false);
          } else { setSelectedTemplateId(''); setShowInlineTemplatePicker(false); setNoTemplateWarning(true); }
        } else { setSelectedTemplateId(d.templateId ? String(d.templateId) : ''); setShowInlineTemplatePicker(false); setNoTemplateWarning(false); }
        // Auto-collapse worklist to give editor more space
        setWorklistCollapsed(true);
      }
    } catch (err) { setErrorMsg(err.response?.data?.message || 'Failed to load report details.'); }
    finally { setActionLoading(false); }
  };

  const handleOpenTechnicianStudy = (item) => { setTechModalTestId(item.testId); setTechModalOrder(item); setShowTechModal(true); };

  const handleSaveTechnicianHandover = async (techData) => {
    try {
      await radiologyResultService.saveTechnicianStudy(techModalTestId, techData);
      setSuccessMsg('Technician acquisition completed. Status: In Review.'); setShowTechModal(false); fetchSummary(); fetchWorklist();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) { setErrorMsg(err.response?.data?.message || 'Failed to save technician study.'); }
  };

  useEffect(() => {
    if (!formData.findings || formData.findings.trim().length < 25 || suggestionIgnored || activeReport?.approved === 1) { setSuggestedImpression(''); return; }
    const timer = setTimeout(() => {
      const suggestion = radiologyQualityChecker.generateSuggestedImpression(formData.findings);
      if (suggestion && (!formData.impression || formData.impression.trim().length < 5)) setSuggestedImpression(suggestion);
      else setSuggestedImpression('');
    }, 600);
    return () => clearTimeout(timer);
  }, [formData.findings, formData.impression, suggestionIgnored, activeReport?.approved]);

  const handleToggleVoice = () => {
    if (isVoiceListening) {
      radiologyVoiceService.stop();
      setIsVoiceListening(false);
      setVoiceTranscript('');
    } else {
      setIsVoiceListening(true);
      setVoiceTranscript('Listening...');
      radiologyVoiceService.start({
        onTranscript: (text, isFinal) => {
          setVoiceTranscript(text);
          if (isFinal) {
            // Insert at the cursor position the doctor last clicked in the editor, not always
            // at the end — clicking the Dictate button itself moves DOM focus away from the
            // editor, so the editor remembers that last cursor position internally.
            if (findingsEditorRef.current) {
              findingsEditorRef.current.insertTextAtCursor(text + ' ');
            } else {
              setFormData(prev => ({ ...prev, findings: (prev.findings ? prev.findings + ' ' : '') + text }));
            }
          }
        },
        onCommand: (commandName, commandLabel) => {
          setVoiceToast(`Command: "${commandLabel}"`);
          setTimeout(() => setVoiceToast(null), 3500);
          if (commandName === 'NEXT_SECTION') setFormData(prev => ({ ...prev, findings: prev.findings + '<p></p>' }));
          else if (commandName === 'INSERT_NORMAL') handleMarkAllNormal();
          else if (commandName === 'MARK_CRITICAL') setFormData(prev => ({ ...prev, isCritical: true }));
          else if (commandName === 'SAVE_DRAFT') handleSaveDraft();
        },
        onError: (err) => { console.warn('Voice error:', err); setIsVoiceListening(false); },
        onStateChange: (active) => { if (!active) setIsVoiceListening(false); }
      });
    }
  };

  const handleAcceptSuggestion = () => {
    if (!suggestedImpression) return;
    setFormData(prev => ({ ...prev, impression: suggestedImpression })); setSuggestedImpression('');
    setSuccessMsg('AI impression accepted.'); setTimeout(() => setSuccessMsg(null), 3500);
  };
  const handleEditSuggestion = () => { if (!suggestedImpression) return; setFormData(prev => ({ ...prev, impression: (prev.impression ? prev.impression + ' ' : '') + suggestedImpression })); setSuggestedImpression(''); };
  const handleIgnoreSuggestion = () => { setSuggestedImpression(''); setSuggestionIgnored(true); };

  const handleConfirmAmendment = async ({ amendmentReason, amendedFindings, amendedImpression }) => {
    if (!activeReport) return;
    setActionLoading(true); setErrorMsg(null);
    try {
      const payload = { amendmentReason, examinationTechnique: formData.examinationTechnique, clinicalHistory: formData.clinicalHistory, findings: amendedFindings, impression: amendedImpression, recommendation: formData.recommendation, isCritical: formData.isCritical, criticalRemarks: formData.criticalRemarks };
      const res = await radiologyResultService.amendReport(activeReport.testId, payload);
      setSuccessMsg(res.message || 'Report amended as new version.'); setShowAmendmentModal(false);
      setActiveReport(prev => ({ ...prev, version: (prev.version || 1) + 1, status: 'Amendment', amendmentReason }));
      setFormData(prev => ({ ...prev, version: (prev.version || 1) + 1, findings: amendedFindings, impression: amendedImpression, amendmentReason }));
      fetchSummary(); fetchWorklist(); setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) { setErrorMsg(err.response?.data?.message || 'Failed to amend report.'); }
    finally { setActionLoading(false); }
  };

  const handleAcknowledgeCritical = async (testId) => {
    setActionLoading(true);
    try {
      const res = await radiologyResultService.acknowledgeCritical(testId, 'Acknowledged');
      setSuccessMsg(res.message || 'Critical finding acknowledged.'); fetchWorklist();
      if (activeReport?.testId === testId) setActiveReport(prev => ({ ...prev, criticalAcknowledgedBy: 'Staff', criticalAcknowledgedDate: new Date() }));
      setTimeout(() => setSuccessMsg(null), 3500);
    } catch (err) { setErrorMsg('Failed to acknowledge critical finding.'); }
    finally { setActionLoading(false); }
  };

  const handleApplySavedView = (viewKey) => {
    setActiveSavedView(viewKey);
    const map = { ALL: { status: 'ALL', priority: 'ALL', patientName: '' }, MY_PENDING: { status: 'PENDING', priority: 'ALL', patientName: '' }, TODAY_CT: { status: 'PENDING', priority: 'ALL', patientName: 'CT' }, MY_USG: { status: 'PENDING', priority: 'ALL', patientName: 'USG' }, EMERGENCY: { status: 'ALL', priority: 'Stat', patientName: '' }, IN_REVIEW: { status: 'IN_REVIEW', priority: 'ALL', patientName: '' }, AMENDMENTS: { status: 'AMENDMENT', priority: 'ALL', patientName: '' } };
    if (map[viewKey]) setFilters(prev => ({ ...prev, ...map[viewKey] }));
    fetchWorklist(1);
  };

  useEffect(() => {
    const hk = (e) => {
      if (activeReport && !activeReport.approved && e.ctrlKey && e.key === 's') { e.preventDefault(); handleSaveDraft(); }
      if (activeReport && (e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 'P')) { e.preventDefault(); setShowPrintModal(true); }
    };
    window.addEventListener('keydown', hk); return () => window.removeEventListener('keydown', hk);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReport, formData]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getStatusClass = (st) => (st || 'pending').toLowerCase().replace(/\s+/g, '_');
  const getPriorityClass = (p) => (p || 'routine').toLowerCase();

  // ── Worklist panel split class ────────────────────────────────────────────
  const splitClass = activeReport
    ? (worklistCollapsed ? 'report-fullscreen' : '')
    : 'worklist-only';

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="radiology-workbench">

      {/* ── Command Bar ─────────────────────────────────────────────────── */}
      <div className="rad-command-bar">
        <div className="rad-title-group">
          <div className="rad-workbench-title">
            <div className="rad-wt-icon">☢</div>
            <span className="rad-wt-text">Radiology Reporting Workstation</span>
            <span className="rad-wt-edition">2026 Edition</span>
          </div>
          <p className="rad-wt-subtitle">Open a study → template loads automatically → edit → approve</p>
        </div>

        {/* Role Toggle */}
        <div className="rad-role-toggle">
          <button type="button" className={`rad-role-btn${roleView === 'DOCTOR' ? ' active' : ''}`} onClick={() => setRoleView('DOCTOR')}>
            👨‍⚕️ Radiologist View
          </button>
          <button type="button" className={`rad-role-btn${roleView === 'TECHNICIAN' ? ' active' : ''}`} onClick={() => setRoleView('TECHNICIAN')}>
            🔬 Technician View
          </button>
        </div>
      </div>

      {/* ── Saved Views Strip & KPI Metrics Banner (only when viewing worklist overview) ── */}
      {!activeReport && (
        <>
          <div className="rad-views-strip">
            <span className="rad-views-label">⚡ Quick Views</span>
            {[{ key: 'ALL', label: '📋 All Orders' }, { key: 'MY_PENDING', label: '⏳ Pending' }, { key: 'TODAY_CT', label: '🔬 CT' }, { key: 'MY_USG', label: '📡 USG' }, { key: 'EMERGENCY', label: '🚨 Stat' }, { key: 'IN_REVIEW', label: '🔍 In Review' }, { key: 'AMENDMENTS', label: '✏️ Amendments' }].map(v => (
              <button key={v.key} type="button" className={`rad-view-chip${activeSavedView === v.key ? ' active' : ''}`} onClick={() => handleApplySavedView(v.key)}>{v.label}</button>
            ))}
          </div>

          <div className="rad-kpi-banner">
            <div className={`rad-kpi-cell${filters.status === 'PENDING' ? ' active' : ''}`} onClick={() => { setFilters(prev => ({ ...prev, status: 'PENDING' })); fetchWorklist(1); }}>
              <div className="rad-kpi-icon-wrap pending">⏳</div>
              <div className="rad-kpi-meta">
                <p className="rad-kpi-label">Pending</p>
                <div className="rad-kpi-value">{summary.pendingResults}</div>
                <p className="rad-kpi-sub">Awaiting report</p>
              </div>
            </div>
            <div className={`rad-kpi-cell${filters.status === 'IN_REVIEW' ? ' active' : ''}`} onClick={() => { setFilters(prev => ({ ...prev, status: 'IN_REVIEW' })); fetchWorklist(1); }}>
              <div className="rad-kpi-icon-wrap review">🔬</div>
              <div className="rad-kpi-meta">
                <p className="rad-kpi-label">In Review</p>
                <div className="rad-kpi-value">{worklist.filter(w => (w.status || '').toLowerCase() === 'in review').length}</div>
                <p className="rad-kpi-sub">Tech → Doctor</p>
              </div>
            </div>
            <div className={`rad-kpi-cell${filters.status === 'DRAFT' ? ' active' : ''}`} onClick={() => { setFilters(prev => ({ ...prev, status: 'DRAFT' })); fetchWorklist(1); }}>
              <div className="rad-kpi-icon-wrap draft">📝</div>
              <div className="rad-kpi-meta">
                <p className="rad-kpi-label">Drafts</p>
                <div className="rad-kpi-value">{summary.drafts}</div>
                <p className="rad-kpi-sub">Saved, not approved</p>
              </div>
            </div>
            <div className={`rad-kpi-cell${filters.status === 'APPROVED' ? ' active' : ''}`} onClick={() => { setFilters(prev => ({ ...prev, status: 'APPROVED' })); fetchWorklist(1); }}>
              <div className="rad-kpi-icon-wrap approved">✅</div>
              <div className="rad-kpi-meta">
                <p className="rad-kpi-label">Finalized Today</p>
                <div className="rad-kpi-value">{summary.approvedToday}</div>
                <p className="rad-kpi-sub">Signed &amp; complete</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Toast Notifications ─────────────────────────────────────────── */}
      {(errorMsg || successMsg) && (
        <div className="rad-toast-container">
          {errorMsg && (
            <div className="rad-toast error">
              <span className="rad-toast-icon">⚠️</span>
              <span className="rad-toast-text"><strong>Error:</strong> {errorMsg}</span>
              <button className="rad-toast-close" onClick={() => setErrorMsg(null)}>×</button>
            </div>
          )}
          {successMsg && (
            <div className="rad-toast success">
              <span className="rad-toast-icon">✅</span>
              <span className="rad-toast-text">{successMsg}</span>
              <button className="rad-toast-close" onClick={() => setSuccessMsg(null)}>×</button>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          MAIN SPLIT PANEL
          ═══════════════════════════════════════════════════════════════════ */}
      <div className={`rad-main-split${splitClass ? ` ${splitClass}` : ''}`} style={{ height: activeReport ? 'calc(100vh - 75px)' : 'calc(100vh - 200px)', minHeight: '550px' }}>

        {/* ── LEFT: Collapsed strip (icon-only mode) ────────────────────── */}
        {activeReport && worklistCollapsed && (
          <div className="rad-collapsed-strip">
            <button type="button" className="rad-collapsed-icon-btn" title="Expand worklist" onClick={() => setWorklistCollapsed(false)}>☰</button>
            <button type="button" className="rad-collapsed-icon-btn" title="All studies" onClick={() => { setWorklistCollapsed(false); fetchWorklist(1); }}>📋</button>
            <button type="button" className="rad-collapsed-icon-btn" title="Pending" onClick={() => { setFilters(prev => ({ ...prev, status: 'PENDING' })); setWorklistCollapsed(false); fetchWorklist(1); }}>⏳</button>
          </div>
        )}

        {/* ── LEFT: Worklist Panel ──────────────────────────────────────── */}
        {(!activeReport || !worklistCollapsed) && (
          <div className="rad-worklist-panel">
            {/* Panel header */}
            <div className="rad-panel-header">
              <div className="rad-panel-title-row">
                <span className="rad-panel-title">{roleView === 'TECHNICIAN' ? 'Technician Worklist' : 'Radiologist Worklist'}</span>
                <span className="rad-panel-count-badge">{totalCount}</span>
                {loading && <span style={{ fontSize: '11px', color: 'var(--rad-cyan)' }}>⟳</span>}
              </div>
              {activeReport && (
                <button type="button" className="rad-panel-collapse-btn" onClick={() => setWorklistCollapsed(true)} title="Collapse worklist">◀</button>
              )}
            </div>

            {/* Filter area */}
            <div className="rad-filter-area">
              <div className="rad-search-row">
                <div className="rad-search-box">
                  <span className="rad-search-icon">🔍</span>
                  <input type="text" className="rad-search-input" placeholder="Search patient, UHID, lab no..." value={filters.patientName} onChange={e => setFilters({ ...filters, patientName: e.target.value })} onKeyDown={e => e.key === 'Enter' && fetchWorklist(1)} />
                </div>
                <button type="button" className="rad-filter-toggle-btn" onClick={() => setShowAdvancedFilters(v => !v)}>
                  {showAdvancedFilters ? '▲ Less' : '▼ Filters'}
                </button>
                <button type="button" className="rad-btn-search" onClick={() => fetchWorklist(1)}>Go</button>
              </div>
              {showAdvancedFilters && (
                <div className="rad-advanced-filters">
                  <div className="rad-filter-field">
                    <label>UHID</label>
                    <input type="text" placeholder="e.g. AUR.0000000004" value={filters.uhid} onChange={e => setFilters({ ...filters, uhid: e.target.value })} onKeyDown={e => e.key === 'Enter' && fetchWorklist(1)} />
                  </div>
                  <div className="rad-filter-field">
                    <label>Priority</label>
                    <select value={filters.priority} onChange={e => setFilters({ ...filters, priority: e.target.value })}>
                      <option value="ALL">All</option>
                      <option value="Routine">Routine</option>
                      <option value="Urgent">Urgent</option>
                      <option value="Stat">Stat</option>
                    </select>
                  </div>
                  <div className="rad-filter-field">
                    <label>From Date</label>
                    <input type="date" value={filters.fromDate} onChange={e => setFilters({ ...filters, fromDate: e.target.value })} />
                  </div>
                  <div className="rad-filter-field">
                    <label>To Date</label>
                    <input type="date" value={filters.toDate} onChange={e => setFilters({ ...filters, toDate: e.target.value })} />
                  </div>
                  <div className="rad-filter-field" style={{ gridColumn: '1 / -1' }}>
                    <div className="rad-filter-actions-row">
                      <button className="rad-btn-search" onClick={() => fetchWorklist(1)}>🔍 Search</button>
                      <button className="rad-btn-reset" onClick={() => { setFilters({ uhid: '', patientName: '', registrationNo: '', barcodeNo: '', status: 'ALL', priority: 'ALL', fromDate: '', toDate: '' }); fetchWorklist(1); }}>Reset</button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Status tab strip */}
            <div className="rad-status-strip">
              {[
                { key: 'ALL', label: 'All' },
                { key: 'PENDING', label: 'Pending' },
                { key: 'IN_REVIEW', label: 'In Review' },
                { key: 'DRAFT', label: 'Draft' },
                { key: 'APPROVED', label: 'Approved' },
                { key: 'CRITICAL', label: '🚨 Critical' }
              ].map(st => (
                <button key={st.key} type="button" className={`rad-status-tab${filters.status === st.key ? ' active' : ''}`} onClick={() => { setFilters(prev => ({ ...prev, status: st.key })); fetchWorklist(1); }}>
                  {st.label}
                </button>
              ))}
            </div>

            {/* Worklist table */}
            <div className="rad-worklist-inner">
              <table className="rad-worklist-table">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>Investigation</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {worklist.length === 0 ? (
                    <tr>
                      <td colSpan="5">
                        <div className="rad-empty-state">
                          <div className="rad-empty-icon">📋</div>
                          <div className="rad-empty-title">{loading ? 'Loading studies...' : 'No studies found'}</div>
                          <div className="rad-empty-desc">{loading ? 'Please wait' : 'Try adjusting your filters or date range.'}</div>
                        </div>
                      </td>
                    </tr>
                  ) : worklist.map(item => {
                    const prio = item.priority || 'Routine';
                    const st = item.status || 'Pending';
                    const isActive = activeReport?.testId === item.testId;
                    const rowClass = `${isActive ? 'active-row' : ''} ${item.isCritical ? 'critical-row' : ''}`.trim();
                    return (
                      <tr key={item.testId} className={rowClass} data-status={(st || '').toLowerCase()} onClick={() => handleOpenReport(item.testId)}>
                        <td>
                          <div className="rad-patient-cell">
                            <div className="wl-patient-name">#{item.testId} · {item.patientName}</div>
                            <div className="wl-patient-meta">{item.gender}, {item.ageString || `${item.ageInDays || 0}d`}</div>
                            <div className="wl-uhid">{item.labNo} | {item.uhid}</div>
                          </div>
                        </td>
                        <td>
                          <div className="wl-investigation-name">{item.itemName}</div>
                          <span className="rad-modality-tag">{item.department || 'Radiology'}</span>
                          {item.hasTechnicianNotes && <div style={{ fontSize: '10px', color: 'var(--rad-blue)', marginTop: '3px' }}>🔬 Tech: {item.technicianName || 'Yes'}</div>}
                        </td>
                        <td>
                          <span className={`rad-priority-pill ${getPriorityClass(prio)}`}>{prio}</span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                            <span className={`rad-status-badge ${getStatusClass(st)}`}>{st}</span>
                            {item.isCritical && <span className="rad-status-badge critical">⚠ CRIT</span>}
                            {item.version > 1 && <span style={{ fontSize: '10px', color: 'var(--rad-amber)', fontWeight: 600 }}>v{item.version}</span>}
                          </div>
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                            {item.isCritical && !item.criticalAcknowledgedBy && (
                              <button className="wl-action-btn ack" onClick={() => handleAcknowledgeCritical(item.testId)}>Ack!</button>
                            )}
                            {roleView === 'TECHNICIAN' && item.approved !== 1
                              ? <button className="wl-action-btn tech" onClick={() => handleOpenTechnicianStudy(item)}>Protocol</button>
                              : <button className="wl-action-btn" onClick={() => handleOpenReport(item.testId)} disabled={actionLoading}>
                                  {item.approved === 1 ? 'View' : st === 'Draft' ? 'Edit' : st === 'In Review' ? 'Report' : 'Enter'}
                                </button>
                            }
                            <button className="wl-audit-btn" onClick={() => handleViewAudit(item.testId)} title="Audit trail">⏱</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Centralized Pagination */}
            <CommonPagination
              page={pageNumber}
              pageSize={pageSize}
              totalRecords={totalCount}
              onPageChange={(p) => {
                setPageNumber(p);
                fetchWorklist(p, pageSize);
              }}
              onPageSizeChange={(s) => {
                setPageSize(s);
                setPageNumber(1);
                fetchWorklist(1, s);
              }}
              loading={loading}
            />
          </div>
        )}

        {/* ── RIGHT: Report Editor Panel ────────────────────────────────── */}
        <div className={`rad-editor-panel${!activeReport ? ' hidden' : ''}`}>
          {activeReport ? (
            <>
              {/* Editor top bar */}
              <div className="rad-editor-topbar">
                <div className="rad-editor-study-title">
                  <div className="rad-editor-study-name">
                    <span>📋</span>
                    {activeReport.itemName}
                    <span className={`rad-editor-status-pill ${getStatusClass(activeReport.status || 'pending')}`}>
                      {activeReport.approved === 1 ? 'Finalized' : (activeReport.status || 'Pending')}
                    </span>
                    {formData.version > 1 && <span className="rad-version-chip">v{formData.version}</span>}
                  </div>
                </div>
                {worklistCollapsed && (
                  <button type="button" className="rad-expand-toggle" onClick={() => setWorklistCollapsed(false)} title="Show worklist">
                    ◀ Worklist
                  </button>
                )}
                <button type="button" className="rad-close-editor-btn" onClick={() => { setActiveReport(null); setWorklistCollapsed(false); }} title="Close report">✕</button>
              </div>

              {/* Patient context strip — collapsible */}
              <div className={`rad-patient-context-strip${patientCtxCollapsed ? ' collapsed' : ''}`}>
                <button type="button" className="rad-ctx-toggle" onClick={() => setPatientCtxCollapsed(v => !v)} title={patientCtxCollapsed ? 'Show details' : 'Collapse details'}>
                  {patientCtxCollapsed ? '▸' : '▾'}
                </button>
                <div className="rad-ctx-item"><span className="rad-ctx-label">Patient</span><span className="rad-ctx-value">{activeReport.patientName}</span></div>
                {!patientCtxCollapsed && <div className="rad-ctx-sep" />}
                {!patientCtxCollapsed && <div className="rad-ctx-item"><span className="rad-ctx-label">UHID</span><span className="rad-ctx-value" style={{ fontFamily: 'monospace', fontSize: '12px' }}>{activeReport.uhid}</span></div>}
                {!patientCtxCollapsed && <div className="rad-ctx-item"><span className="rad-ctx-label">Lab No</span><span className="rad-ctx-value" style={{ fontFamily: 'monospace', fontSize: '12px' }}>{activeReport.labNo}</span></div>}
                {!patientCtxCollapsed && <div className="rad-ctx-sep" />}
                <div className="rad-ctx-item"><span className="rad-ctx-label">Age / Sex</span><span className="rad-ctx-value">{activeReport.ageString || 'Adult'} / {activeReport.gender}</span></div>
                {!patientCtxCollapsed && <div className="rad-ctx-sep" />}
                {!patientCtxCollapsed && <div className="rad-ctx-item"><span className="rad-ctx-label">Ref. Doctor</span><span className="rad-ctx-value">{activeReport.doctorName || 'Self'}</span></div>}
                {!patientCtxCollapsed && <div className="rad-ctx-sep" />}
                <div className="rad-ctx-item"><span className="rad-ctx-label">Modality</span><span className="rad-ctx-value">{activeReport.department}</span></div>
                {!patientCtxCollapsed && <div className="rad-ctx-sep" />}
                {!patientCtxCollapsed && <div className="rad-ctx-item"><span className="rad-ctx-label">Date</span><span className="rad-ctx-value">{activeReport.registrationDate ? new Date(activeReport.registrationDate).toLocaleDateString('en-IN') : '—'}</span></div>}
              </div>

              {/* Status lifecycle timeline */}
              {(() => {
                const status = (activeReport.status || 'Pending').toLowerCase();
                const steps = [
                  { key: 'ordered', label: 'Ordered', icon: '📋' },
                  { key: 'tech', label: 'Tech', icon: '🔬' },
                  { key: 'draft', label: 'Draft', icon: '📝' },
                  { key: 'approved', label: 'Approved', icon: '✅' }
                ];
                let activeIdx = 0;
                if (status.includes('review') || activeReport.hasTechnicianNotes) activeIdx = 1;
                if (status === 'draft' || (activeReport.findings && !activeReport.approved)) activeIdx = 2;
                if (status === 'approved' || activeReport.approved === 1) activeIdx = 3;
                return (
                  <div className="rad-status-timeline">
                    {steps.map((s, i) => (
                      <React.Fragment key={s.key}>
                        <div className={`rad-tl-step${i <= activeIdx ? ' done' : ''}${i === activeIdx ? ' current' : ''}`}>
                          <span className="rad-tl-icon">{s.icon}</span>
                          <span className="rad-tl-label">{s.label}</span>
                        </div>
                        {i < steps.length - 1 && <div className={`rad-tl-connector${i < activeIdx ? ' done' : ''}`} />}
                      </React.Fragment>
                    ))}
                  </div>
                );
              })()}

              {/* Technician handover info */}
              {(activeReport.protocolName || activeReport.technicianNotes || activeReport.technicianMeasurements) && (
                <div className="rad-tech-strip">
                  <span className="rad-tech-strip-label">🔬 Tech Handover</span>
                  {activeReport.protocolName && <span><strong>Protocol:</strong> {activeReport.protocolName}</span>}
                  {activeReport.technicianMeasurements && <span><strong>Measurements:</strong> {activeReport.technicianMeasurements}</span>}
                  {activeReport.technicianNotes && <span><strong>Notes:</strong> {activeReport.technicianNotes}</span>}
                  <span style={{ marginLeft: 'auto', color: 'var(--rad-text-dim)', fontSize: '11px' }}>By: {activeReport.technicianName || 'Tech'}</span>
                </div>
              )}

              {/* Finalized banner */}
              {activeReport.approved === 1 && (
                <div className="rad-finalized-banner">
                  <div className="rad-final-text">
                    ✅ <strong>FINALIZED:</strong> Approved by <strong>{activeReport.approvedBy || 'Radiologist'}</strong> on {activeReport.approvedDate ? new Date(activeReport.approvedDate).toLocaleString('en-IN') : 'N/A'}.
                    {activeReport.version > 1 && <span style={{ marginLeft: '6px', color: 'var(--rad-amber)', fontWeight: 700 }}>(v{activeReport.version})</span>}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="rad-revise-btn" onClick={() => setShowRevisionModal(true)}>Minor Revision</button>
                    <button className="rad-amend-btn" onClick={() => setShowAmendmentModal(true)}>Formal Amendment (v{(activeReport.version || 1) + 1})</button>
                  </div>
                </div>
              )}

              {/* Copied-from notice */}
              {copiedFromNotice && (
                <div className="rad-copied-banner">
                  <span><strong>Notice:</strong> {copiedFromNotice}</span>
                  <button type="button" onClick={() => setCopiedFromNotice(null)}>×</button>
                </div>
              )}

              {/* Tool toolbar */}
              {activeReport.approved !== 1 && (
                <div className="rad-tool-toolbar">
                  {/* Template chip */}
                  <div className="rad-tmpl-chip">
                    <span className="rad-tmpl-chip-label">Template:</span>
                    {formData.templateName
                      ? <span className="rad-tmpl-chip-name" title={formData.templateName}>{formData.templateName}</span>
                      : <span className="rad-tmpl-chip-none">None selected</span>
                    }
                  </div>
                  {(activeReport.templates || []).length > 0 && (
                    <button type="button" className="rad-tool-btn accent" onClick={() => { setShowInlineTemplatePicker(true); setInlineTemplateSearch(''); }}>
                      {formData.templateName ? '↔ Change' : '+ Template'}
                    </button>
                  )}
                  <div className="rad-tool-separator" />
                  <button type="button" className={`rad-tool-btn${isVoiceListening ? ' voice-active' : ''}`} onClick={handleToggleVoice} title="Voice dictation">
                    🎤 {isVoiceListening ? 'Stop Dictation' : 'Dictate'}
                  </button>
                  <button type="button" className="rad-tool-btn" onClick={() => setShowPrevReportsModal(true)} title="Load prior report">📂 Prior Reports</button>
                  <button type="button" className="rad-tool-btn" onClick={() => setShowStructuredDrawer(true)} title="Structured findings builder">🧩 Structured</button>
                  <button type="button" className="rad-tool-btn" onClick={() => handleMarkAllNormal()} title="Insert normal findings">✓ Mark Normal</button>
                  <div className="rad-tool-separator" />
                  <button type="button" className="rad-tool-btn" onClick={() => fileInputRef.current?.click()} title="Import from Word document">⬆ Import DOCX</button>
                  <button type="button" className="rad-tool-btn" onClick={() => setShowPreferencesModal(true)} title="Doctor reporting preferences">⚙ Preferences</button>
                </div>
              )}

              {/* Voice dictation live bar */}
              {isVoiceListening && (
                <div className="rad-voice-active-bar">
                  <span className="rad-voice-pulse">●</span>
                  <span className="rad-voice-live-text">{voiceTranscript || 'Listening... speak findings'}</span>
                  {voiceToast && <span className="rad-voice-cmd-toast">{voiceToast}</span>}
                </div>
              )}

              {/* Inline template picker */}
              {showInlineTemplatePicker && !activeReport.approved && (
                <div className="rad-inline-picker">
                  <div className="rad-itp-header">
                    <span>Select Template for {activeReport.itemName}</span>
                    <button type="button" onClick={() => setShowInlineTemplatePicker(false)}>×</button>
                  </div>
                  <input type="text" className="rad-itp-search" placeholder="Search templates..." value={inlineTemplateSearch} onChange={e => setInlineTemplateSearch(e.target.value)} autoFocus />
                  <div className="rad-itp-list">
                    {(activeReport.templates || []).filter(t => !inlineTemplateSearch || (t.title || t.templateTitle || '').toLowerCase().includes(inlineTemplateSearch.toLowerCase())).map(t => (
                      <button key={t.templateId} type="button" className={`rad-itp-item${String(selectedTemplateId) === String(t.templateId) ? ' selected' : ''}`} onClick={() => { handleSelectTemplate(t); setShowInlineTemplatePicker(false); setInlineTemplateSearch(''); }}>
                        <span className="rad-itp-radio">{String(selectedTemplateId) === String(t.templateId) ? '●' : '○'}</span>
                        <span className="rad-itp-name">{t.title || t.templateTitle}</span>
                        {t.isDefault && <span className="rad-itp-badge">Default</span>}
                      </button>
                    ))}
                    {(activeReport.templates || []).filter(t => !inlineTemplateSearch || (t.title || t.templateTitle || '').toLowerCase().includes(inlineTemplateSearch.toLowerCase())).length === 0 && (
                      <div className="rad-itp-empty">No templates match.</div>
                    )}
                  </div>
                </div>
              )}

              {/* No template warning */}
              {noTemplateWarning && (
                <div className="rad-no-template-warning">
                  <span style={{ fontSize: '16px' }}>⚠️</span>
                  <div>
                    <strong style={{ fontSize: '12px', color: 'var(--rad-amber)' }}>No report template configured for {activeReport.itemName}.</strong>
                    <div style={{ fontSize: '11px', color: 'var(--rad-text-muted)', marginTop: '2px' }}>Ask your administrator to configure a template in the Radiology Template Master.</div>
                  </div>
                  <button type="button" className="rad-no-tmpl-btn" onClick={() => setNoTemplateWarning(false)}>Continue blank</button>
                </div>
              )}

              {/* ── Report completeness indicator ──────────────────────── */}
              {activeReport.approved !== 1 && (() => {
                const checks = [
                  { key: 'findings', label: 'Findings', ok: !!(formData.findings && formData.findings.replace(/<[^>]*>/g, '').trim().length > 5) },
                  { key: 'impression', label: 'Impression', ok: !!(formData.impression && formData.impression.replace(/<[^>]*>/g, '').trim().length > 3) },
                  { key: 'template', label: 'Template', ok: !!formData.templateId },
                ];
                const done = checks.filter(c => c.ok).length;
                const pct = Math.round((done / checks.length) * 100);
                const cls = pct === 100 ? 'full' : pct >= 66 ? 'high' : pct >= 33 ? 'medium' : 'low';
                return (
                  <div className="rad-completeness-bar">
                    <span className="rad-completeness-label">Completeness</span>
                    <div className="rad-completeness-track">
                      <div className={`rad-completeness-fill ${cls}`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="rad-completeness-steps">
                      {checks.map(c => (
                        <span key={c.key} className={`rad-completeness-step ${c.ok ? 'done' : 'pending'}`}>
                          {c.ok ? '✓' : '○'} {c.label}
                        </span>
                      ))}
                    </div>
                    <span className="rad-completeness-pct" style={{ color: pct === 100 ? '#059669' : pct >= 66 ? '#2563eb' : '#d97706' }}>{pct}%</span>
                  </div>
                );
              })()}

              {/* ── Editor scrollable content ──────────────────────────── */}
              <div className="rad-editor-body">

                {/* Findings section */}
                <div className="rad-editor-section">
                  <label className="rad-editor-label">
                    📄 Detailed Radiological Findings <span className="required">*</span>
                  </label>
                  <RadiologyRichEditor
                    ref={findingsEditorRef}
                    value={formData.findings}
                    disabled={activeReport.approved === 1}
                    onChange={htmlVal => setFormData(prev => ({ ...prev, findings: htmlVal }))}
                    placeholder="Report findings appear here. When a template is loaded, all sections (Clinical History, Technique, Findings, Impression, Recommendation) are in one unified document."
                    minHeight="480px"
                    macros={resolvedConfig?.effectiveProfile?.macros || []}
                  />
                </div>

                {/* AI Impression suggestion */}
                {suggestedImpression && activeReport.approved !== 1 && (
                  <div className="rad-ai-suggestion-box">
                    <div className="rad-ai-header">
                      <div className="rad-ai-title">
                        <span>🤖</span> <span>AI Suggested Impression</span>
                      </div>
                      <div className="rad-ai-disclaimer">For clinical review only — not auto-applied</div>
                    </div>
                    <div className="rad-ai-content">{suggestedImpression}</div>
                    <div className="rad-ai-actions">
                      <button type="button" className="rad-ai-btn-accept" onClick={handleAcceptSuggestion}>✓ Accept</button>
                      <button type="button" className="rad-ai-btn-ignore" onClick={handleEditSuggestion}>↓ Append</button>
                      <button type="button" className="rad-ai-btn-ignore" onClick={handleIgnoreSuggestion}>✕ Ignore</button>
                    </div>
                  </div>
                )}

              </div>

              {/* Critical finding toggle */}
              <div className={`rad-critical-inline${formData.isCritical ? ' active' : ''}`}>
                <label className="rad-critical-label">
                  <input type="checkbox" checked={formData.isCritical} disabled={activeReport.approved === 1} onChange={e => setFormData(prev => ({ ...prev, isCritical: e.target.checked }))} />
                  <span>🚨 Critical Diagnostic Finding — requires immediate clinical notification</span>
                </label>
                {formData.isCritical && (
                  <input type="text" className="rad-critical-remarks-input" placeholder="Notification details (e.g. communicated to Dr. Sharma at 17:30)" value={formData.criticalRemarks} disabled={activeReport.approved === 1} onChange={e => setFormData(prev => ({ ...prev, criticalRemarks: e.target.value }))} />
                )}
                {activeReport.criticalAcknowledgedBy && (
                  <span className="rad-critical-ack-badge">✅ Acknowledged by {activeReport.criticalAcknowledgedBy}</span>
                )}
              </div>

              {/* Editor footer */}
              <div className="rad-editor-footer-bar">
                <div className="rad-footer-left">
                  <button className="rad-btn-print-preview" onClick={() => setShowPrintModal(true)}>🖨 Print Preview</button>
                  <button className="rad-btn-view" onClick={() => handleViewAudit(activeReport.testId)}>⏱ Audit History</button>
                </div>
                <div className="rad-footer-right">
                  {activeReport.approved !== 1 && (
                    <>
                      <button className="rad-btn-draft" disabled={actionLoading} onClick={handleSaveDraft} title="Ctrl+S">
                        {actionLoading ? '⟳ Saving...' : <><span>💾 Save Draft</span><span className="rad-kbd">Ctrl+S</span></>}
                      </button>
                      <button className="rad-btn-finalize" disabled={actionLoading} onClick={handleSaveAndApprove}>
                        {actionLoading ? '⟳ Approving...' : <><span>✅ Save & Approve</span></>}
                      </button>
                    </>
                  )}
                  <button className="rad-btn-reset" onClick={() => { setActiveReport(null); setWorklistCollapsed(false); }}>Close</button>
                </div>
              </div>
            </>
          ) : (
            /* No report selected — placeholder */
            <div className="rad-no-report-state">
              <div className="rad-no-report-icon">☢</div>
              <div className="rad-no-report-title">Radiology Report Editor</div>
              <div className="rad-no-report-desc">
                Select a study from the worklist on the left to open it in the reporting workstation. The template will load automatically.
              </div>
              <div className="rad-no-report-hints">
                <div className="rad-hint-chip"><strong>Ctrl+S</strong> — Save draft at any time</div>
                <div className="rad-hint-chip"><strong>🎤 Dictate</strong> — Voice-to-text findings entry</div>
                <div className="rad-hint-chip"><strong>🤖 AI</strong> — Impression suggested from findings</div>
                <div className="rad-hint-chip"><strong>📂 Prior Reports</strong> — Copy from previous study</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          MODALS (Revision, Audit, Print, Amendment, etc.)
          ═══════════════════════════════════════════════════════════════════ */}

      {/* Revision modal */}
      {showRevisionModal && (
        <div className="rad-modal-overlay">
          <div className="rad-modal-container" style={{ maxWidth: '600px' }}>
            <div className="rad-modal-header">
              <h2>Clinical Revision Justification</h2>
              <button className="rad-modal-close" onClick={() => setShowRevisionModal(false)}>×</button>
            </div>
            <div className="rad-modal-body">
              <p className="rad-modal-desc">Modifying a finalized report requires mandatory justification. This action is permanently audited.</p>
              <div className="rad-modal-field">
                <label>Revision Reason <span style={{ color: 'var(--rad-red)' }}>*</span></label>
                <textarea rows="3" placeholder="e.g. Addendum to clarify finding..." value={formData.revisionReason} onChange={e => setFormData({ ...formData, revisionReason: e.target.value })} />
              </div>
            </div>
            <div className="rad-modal-footer">
              <button className="rad-btn-reset" onClick={() => setShowRevisionModal(false)}>Cancel</button>
              <button className="rad-btn-finalize" disabled={actionLoading} onClick={handleConfirmRevision}>{actionLoading ? 'Saving...' : 'Confirm & Save Revision'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Print preview modal */}
      {showPrintModal && activeReport && (
        <div className="rad-modal-overlay">
          <div className="rad-print-container">
            <div className="rad-print-header">
              <div className="rad-print-clinic-name">AUROLAB DIAGNOSTIC IMAGING SERVICES</div>
              <div className="rad-print-subtitle">State-of-the-Art Radiology and Molecular Imaging Center</div>
              <div className="rad-print-report-title">DIAGNOSTIC RADIOLOGY REPORT ({activeReport.approved === 1 ? 'FINAL' : 'PRELIMINARY'})</div>
            </div>
            <table className="rad-print-demographics"><tbody>
              <tr><td><strong>Patient Name:</strong> {activeReport.patientName}</td><td><strong>UHID:</strong> {activeReport.uhid}</td></tr>
              <tr><td><strong>Age / Gender:</strong> {activeReport.ageString || 'Adult'} / {activeReport.gender}</td><td><strong>Lab / Reg No:</strong> {activeReport.labNo}</td></tr>
              <tr><td><strong>Referring Doctor:</strong> {activeReport.doctorName || 'Self / Hospital'}</td><td><strong>Date of Exam:</strong> {new Date(activeReport.registrationDate).toLocaleDateString('en-IN')}</td></tr>
              <tr><td><strong>Investigation:</strong> {activeReport.itemName}</td><td><strong>Modality:</strong> {activeReport.department}</td></tr>
            </tbody></table>
            <div className="rad-print-body">
              {formData.examinationTechnique && <><div className="rad-print-section-title">Technique</div><div className="rad-print-text">{formData.examinationTechnique}</div></>}
              {formData.clinicalHistory && <><div className="rad-print-section-title">Clinical History</div><div className="rad-print-text">{formData.clinicalHistory}</div></>}
              <div className="rad-print-section-title">Findings</div>
              <div className="rad-print-html" dangerouslySetInnerHTML={{ __html: sanitizeHtml(formData.findings || '<p>No findings recorded.</p>') }} />
              <div className="rad-print-section-title">Impression</div>
              <div className="rad-print-impression rad-print-html" dangerouslySetInnerHTML={{ __html: sanitizeHtml(formData.impression || '<p>Pending diagnostic impression.</p>') }} />
              {formData.recommendation && <><div className="rad-print-section-title">Recommendation</div><div className="rad-print-text">{formData.recommendation}</div></>}
            </div>
            <div className="rad-print-signature">
              <div className="rad-digital-sig-stamp">
                {activeReport.doctorSignatureUrl && (
                  <div style={{ marginBottom: '6px' }}>
                    <img
                      src={activeReport.doctorSignatureUrl}
                      alt="Doctor Signature"
                      style={{ maxHeight: '50px', maxWidth: '160px', objectFit: 'contain' }}
                    />
                  </div>
                )}
                <div className="rad-sig-doc-name">{activeReport.approvedBy || activeReport.doctorName || 'Consultant Radiologist'}</div>
                <div className="rad-sig-qualification">{activeReport.doctorQualification || 'MBBS, MD, DMRD (Radiodiagnosis)'}</div>
                <div className="rad-sig-reg">Reg No: {activeReport.doctorRegistrationNo || 'MCI / State Medical Council'}</div>
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>Digitally Approved: {activeReport.approvedDate ? new Date(activeReport.approvedDate).toLocaleString('en-IN') : 'Preliminary Draft (Unsigned)'}</div>
                {activeReport.approved === 1 && <div className="rad-sig-hash">Verified: SHA256-RAD-{activeReport.testId}-{activeReport.version || 1}</div>}
              </div>
            </div>
            <div className="rad-print-controls">
              <button type="button" className="rad-btn-docx" disabled={exportingDocx} onClick={() => handleExportDocx(activeReport.testId)}>{exportingDocx ? 'Exporting...' : '⬇ Download Word (.docx)'}</button>
              <button className="rad-btn-search" onClick={() => window.print()}>🖨 Print Report</button>
              <button className="rad-btn-reset" onClick={() => setShowPrintModal(false)}>Close Preview</button>
            </div>
          </div>
        </div>
      )}

      {/* Audit History modal */}
      {showAuditModal && (
        <div className="rad-modal-overlay">
          <div className="rad-modal-container" style={{ maxWidth: '700px' }}>
            <div className="rad-modal-header">
              <h2>⏱ Radiology Status & Revision Audit History</h2>
              <button className="rad-modal-close" onClick={() => setShowAuditModal(false)}>×</button>
            </div>
            <div className="rad-modal-body">
              {auditTrail.length === 0 ? (
                <p style={{ color: 'var(--rad-text-muted)', fontSize: '13px' }}>No status transitions recorded yet.</p>
              ) : (
                <table className="rad-table">
                  <thead><tr><th>Date / Time</th><th>Action</th><th>Status Transition</th><th>Remarks</th><th>User</th></tr></thead>
                  <tbody>{auditTrail.map(a => (
                    <tr key={a.id}>
                      <td>{new Date(a.changedDate).toLocaleString('en-IN')}</td>
                      <td><strong>{a.action}</strong></td>
                      <td>{a.oldStatus} → {a.newStatus}</td>
                      <td>{a.remarks}</td>
                      <td>{a.changedBy}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>
            <div className="rad-modal-footer">
              <button className="rad-btn-reset" onClick={() => setShowAuditModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Component modals (unchanged) */}
      <RadiologyTemplatePickerModal isOpen={showTemplatePickerModal} onClose={() => setShowTemplatePickerModal(false)} investigationId={activeReport?.investigationId} investigationName={activeReport?.itemName} doctorName={activeReport?.doctorName} doctorId={activeReport?.doctorId} initialTemplates={activeReport?.templates || []} onSelectTemplate={handleSelectTemplate} onStartBlank={handleStartBlank} />
      <DoctorRadiologyPreferencesModal isOpen={showPreferencesModal} onClose={() => setShowPreferencesModal(false)} doctorId={activeReport?.doctorId} doctorName={activeReport?.doctorName || 'Radiologist'} centreId={activeReport?.centreId} centreName={`Centre #${activeReport?.centreId || 1}`} investigationId={activeReport?.investigationId} investigationName={activeReport?.itemName} availableTemplates={activeReport?.templates || []} onPreferencesSaved={() => { setSuccessMsg('Doctor preferences saved.'); setTimeout(() => setSuccessMsg(null), 3500); }} />
      <RadiologyTechnicianStudyModal isOpen={showTechModal} onClose={() => setShowTechModal(false)} testId={techModalTestId} orderInfo={techModalOrder} initialData={{}} onSaveAndHandover={handleSaveTechnicianHandover} />
      <RadiologyPreviousReportsModal isOpen={showPrevReportsModal} onClose={() => setShowPrevReportsModal(false)} testId={activeReport?.testId} patientName={activeReport?.patientName} uhid={activeReport?.uhid} onCopyReport={handleCopyPreviousReport} />
      <RadiologyStructuredFindingsDrawer isOpen={showStructuredDrawer} onClose={() => setShowStructuredDrawer(false)} department={activeReport?.department} itemName={activeReport?.itemName} onInsertFindings={handleInsertStructuredFindings} onMarkAllNormal={handleMarkAllNormal} />
      <RadiologyBulkDocxModal isOpen={showBulkDocxModal} onClose={() => setShowBulkDocxModal(false)} centreId={activeReport?.centreId || 1} doctorId={activeReport?.doctorId} investigationId={activeReport?.investigationId} onImportSuccess={() => { setSuccessMsg('Bulk DOCX templates imported.'); fetchWorklist(); setTimeout(() => setSuccessMsg(null), 4000); }} />
      <RadiologyQualityCheckModal isOpen={showQualityModal} onClose={() => setShowQualityModal(false)} qualityResult={radiologyQualityChecker.validateReport(formData)} onProceedToSignOff={() => { setShowQualityModal(false); setShowPrintModal(true); }} />
      <RadiologyAmendmentModal isOpen={showAmendmentModal} onClose={() => setShowAmendmentModal(false)} activeReport={activeReport} currentFindings={formData.findings} currentImpression={formData.impression} onConfirmAmendment={handleConfirmAmendment} />

      <input type="file" accept=".docx" ref={fileInputRef} style={{ display: 'none' }} onChange={handleDocxFileChange} />
      <span style={{ display: 'none' }} aria-hidden="true">{recentTemplates.length}{favoriteTemplateIds.length}</span>
    </div>
  );
}
